// Общее для кнопок импорта через сервис epgimport: «Импорт» в форме канала
// и «Импорт EPG (сервис)» в списке каналов (план epgimport §6).
// t(key) — перевод строки из message.js без префикса "message.".

import Vue from "vue";

const FINISHED = ["succeeded", "partial", "failed", "cancelled"];

// Статусы каналов, которые не считаются ошибкой (как failedChannels в сервисе).
const CHANNEL_OK = ["ok", "no_future_programmes", "no_programmes"];

export function isFinished(run) {
  return FINISHED.includes(run.status);
}

export function isDryRun(run) {
  return run.kind === "programs_dryrun";
}

export function hhmm(iso) {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  const pad = n => (n < 10 ? "0" : "") + n;
  return pad(d.getHours()) + ":" + pad(d.getMinutes());
}

// «19.10.2026 08:03» — запуск по расписанию мог быть несколько дней назад.
export function dateHhmm(iso) {
  if (!iso) {
    return "";
  }
  const d = new Date(iso);
  const pad = n => (n < 10 ? "0" : "") + n;
  return pad(d.getDate()) + "." + pad(d.getMonth() + 1) + "." + d.getFullYear() + " " + hhmm(iso);
}

// Окончание запуска: в тот же день — только время, иначе с датой.
function finishTime(run) {
  if (!run.started_at || !run.finished_at) {
    return hhmm(run.finished_at);
  }
  const s = new Date(run.started_at);
  const f = new Date(run.finished_at);
  return s.toDateString() === f.toDateString() ? hhmm(run.finished_at) : dateHhmm(run.finished_at);
}

export function failedChannels(run) {
  const byStatus = (run.summary && run.summary.totals && run.summary.totals.by_status) || {};
  return Object.keys(byStatus)
    .filter(k => !CHANNEL_OK.includes(k))
    .reduce((n, k) => n + byStatus[k], 0);
}

// Каталог каналов (этап 2б): запуски channels и channels_dryrun.
export function isCatalog(run) {
  return run.kind === "channels" || run.kind === "channels_dryrun";
}

// Источники каталога с ошибкой (не ok и не отключённый обработчик).
function failedSources(run) {
  const byStatus = (run.summary && run.summary.catalog_totals && run.summary.catalog_totals.by_status) || {};
  return Object.keys(byStatus)
    .filter(k => k !== "ok" && k !== "unsupported_handler")
    .reduce((n, k) => n + byStatus[k], 0);
}

// Итог запуска каталога — строки всплывающего сообщения: по источникам
// «новых N, выключено N, вернулось N» (при проверке — «было бы»).
export function catalogLines(run, t) {
  const dry = run.kind === "channels_dryrun";
  const totals = (run.summary && run.summary.catalog_totals) || {};
  const lines = [
    t(dry ? "Импорт_EPG_сервис_каталог_проверка_завершена" : "Импорт_EPG_сервис_каталог_завершён") + duration(run, t)
  ];
  if (run.error) {
    lines.push(run.error);
  }
  const counts = r =>
    (dry ? t("Импорт_EPG_сервис_было_бы") + ": " : "") +
    t("Импорт_EPG_сервис_новых") + " " + (r.new || 0) + ", " +
    t("Импорт_EPG_сервис_выключено") + " " + (r.disabled || 0) + ", " +
    t("Импорт_EPG_сервис_вернулось") + " " + (r.reenabled || 0);
  ((run.summary && run.summary.catalog) || [])
    .filter(c => c.status !== "unsupported_handler")
    .forEach(c => {
      lines.push(c.source_name + ": " + (c.status === "ok" ? counts(c) : c.status + (c.message ? " — " + c.message : "")));
    });
  lines.push(t("Импорт_EPG_сервис_итого") + ": " + counts(totals));
  return lines;
}

// Что делает запуск: «проверка всех каналов», «импорт канала 22»,
// «обновление каталога каналов». run — состояние запуска или тело 409.
export function runTitle(run, t) {
  switch (run.kind) {
    case "programs_dryrun":
      return t("Импорт_EPG_сервис_вид_проверка") + " " + runScope(run, t);
    case "channels":
      return t("Импорт_EPG_сервис_вид_каталог");
    case "channels_dryrun":
      return t("Импорт_EPG_сервис_вид_проверка_каталога");
    case "purge":
      return t("Импорт_EPG_сервис_вид_очистка");
    default:
      return t("Импорт_EPG_сервис_вид_импорт") + " " + runScope(run, t);
  }
}

function runScope(run, t) {
  const ids = run.channel_ids || [];
  if (!ids.length) {
    return t("Импорт_EPG_сервис_всех_каналов");
  }
  return t(ids.length === 1 ? "Импорт_EPG_сервис_канала" : "Импорт_EPG_сервис_каналов_род") + " " + ids.join(", ");
}

// «Идёт проверка всех каналов с 19.10.2026 10:15» /
// «Последний запуск 19.10.2026 10:15–10:17: проверка всех каналов — успешно».
export function stateLine(run, t) {
  if (!isFinished(run)) {
    return t("Импорт_EPG_сервис_идёт") + " " + runTitle(run, t) + " " +
      t("Импорт_EPG_сервис_с_времени") + " " + dateHhmm(run.started_at);
  }
  let result;
  switch (run.status) {
    case "succeeded":
      result = t("Импорт_EPG_сервис_успешно");
      break;
    case "partial":
      result = isCatalog(run)
        ? t("Импорт_EPG_сервис_с_ошибками") + " (" + failedSources(run) + " " + t("Импорт_EPG_сервис_источников_мн") + ")"
        : t("Импорт_EPG_сервис_с_ошибками") + " (" + failedChannels(run) + " " + t("Импорт_EPG_сервис_каналов_мн") + ")";
      break;
    case "cancelled":
      // Причина: «остановлен вручную», «остановка сервиса»,
      // «сервис остановлен во время запуска».
      result = t("Импорт_EPG_сервис_прерван") + (run.error ? " (" + run.error + ")" : "");
      break;
    default:
      result = t("Импорт_EPG_сервис_ошибка") + (run.error ? ": " + run.error : "");
  }
  return t("Импорт_EPG_сервис_последний_запуск") + " " +
    dateHhmm(run.started_at) + "–" + finishTime(run) + ": " +
    runTitle(run, t) + " — " + result;
}

// Кнопка «Остановить» (этап 3): подтверждение, отмена через ядро, сообщение.
// vm — компонент панели ($dialog, $noname); после ответа — onDone() для
// продолжения опроса. 409 — запуск уже завершён, тоже onDone().
export function cancelRun(vm, run, t, onDone) {
  return vm.$dialog
    .confirm(t("Импорт_EPG_сервис_остановить") + ": " + runTitle(run, t) + "?")
    .then(() =>
      vm.$noname
        .postChild("channels", "programs_import_service", run.run_id + "/cancel", {})
        .then(() => {
          Vue.notify({ group: "notify", text: t("Импорт_EPG_сервис_остановка_принята"), type: "success" });
          onDone();
        })
        .catch(error => {
          const status = error.response && error.response.status;
          const data = (error.response && error.response.data) || {};
          if (status === 409 || status === 404) {
            Vue.notify({ group: "notify", text: data.msg || t("Импорт_EPG_сервис_состояние_недоступно"), type: "warn" });
            onDone();
          } else if (status === 503) {
            Vue.notify({ group: "notify", text: t("Сервис_импорта_недоступен"), type: "error" });
          } else if (status === 403) {
            Vue.notify({ group: "notify", text: t("Нет_права_на_импорт"), type: "error" });
          } else {
            vm.$noname.HandleError(error);
          }
        })
    )
    .catch(() => {}); // «Отмена» в окне подтверждения
}

function duration(run, t) {
  if (!run.started_at || !run.finished_at) {
    return "";
  }
  const sec = Math.round((new Date(run.finished_at) - new Date(run.started_at)) / 1000);
  return " " + t("Импорт_EPG_сервис_за") + " " + Math.floor(sec / 60) + " " +
    t("Импорт_EPG_сервис_мин") + " " + (sec % 60) + " " + t("Импорт_EPG_сервис_с");
}

function counts(r, dry, t) {
  const sfx = dry ? "_бы" : "";
  let text =
    t("Импорт_EPG_сервис_вставлено" + sfx) + " " + (r.inserted || 0) + ", " +
    t("Импорт_EPG_сервис_удалено" + sfx) + " " + (r.deleted || 0) + ", " +
    t("Импорт_EPG_сервис_хвост") + " " + (r.tail_kept || 0);
  // Прошлое, заменённое передачами источника (редакция 2026-10-26).
  if (r.past_inserted || r.past_deleted) {
    text +=
      "; " + t("Импорт_EPG_сервис_в_прошлом") + ": " +
      t("Импорт_EPG_сервис_вставлено" + sfx) + " " + (r.past_inserted || 0) + ", " +
      t("Импорт_EPG_сервис_удалено" + sfx) + " " + (r.past_deleted || 0);
  }
  return text;
}

// Итог запуска по всем каналам — строки всплывающего сообщения.
export function summaryLines(run, t) {
  const dry = isDryRun(run);
  const totals = (run.summary && run.summary.totals) || {};
  const byStatus = totals.by_status || {};
  const lines = [
    t(dry ? "Импорт_EPG_сервис_проверка_завершена" : "Импорт_EPG_сервис_завершён") + duration(run, t)
  ];
  if (run.error) {
    lines.push(run.error);
  }
  lines.push(
    t("Импорт_EPG_сервис_каналов") + " " + (totals.channels || 0) + ": " +
      Object.keys(byStatus).sort().map(k => k + " " + byStatus[k]).join(", ")
  );
  lines.push(counts(totals, dry, t));
  const bad = ((run.summary && run.summary.channels) || [])
    .filter(c => c.status !== "ok")
    .map(c => c.channel_id + (c.channel_name ? " " + c.channel_name : "") + " — " + c.status);
  if (bad.length) {
    let text = t("Импорт_EPG_сервис_не_ok") + ": " + bad.slice(0, 10).join(", ");
    if (bad.length > 10) {
      text += " " + t("Импорт_EPG_сервис_и_ещё") + " " + (bad.length - 10);
    }
    lines.push(text);
  }
  return lines;
}

// Итог канала: из ядра приходит summary.channels только с этим каналом.
export function channelResult(run) {
  const list = (run.summary && run.summary.channels) || [];
  return list.length ? list[0] : null;
}

// «ok: вставлено бы 86, удалено бы 86, хвост сохранён 0».
export function channelLine(run, t) {
  const r = channelResult(run);
  if (!r) {
    return "";
  }
  let text = r.status + ": " + counts(r, isDryRun(run), t);
  if (r.message) {
    text += " — " + r.message;
  }
  return text;
}



// WEBPACK FOOTER //
// ./src/components/modules/epgImportService.js